import { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert, Snackbar } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';

import Add from '../../components/Add';
import Export from '../../components/Export';
import {
    ALL_DEVICES_ID,
    ManageGroupDevicesModal,
    RealtimeCardGrid,
    filterVisibleRecords,
    useCardReorder,
    useDeviceGroups,
    useGroupMembers,
    useRealtimeQuery,
} from '../../features/Realtime';
import { temperatureContext } from '../../lib/context';
import RealtimeToolbarSection from './RealtimeToolbarSection';

const NO_CARDS: any[] = [];

function RealTime({ isVendor = false }: { isVendor?: boolean }) {
    const { t } = useTranslation(undefined, { keyPrefix: 'RealTime' });
    const tempUnit = useContext(temperatureContext);
    const isMobile = useMediaQuery('(max-width: 768px)');

    const [activeTab, setActiveTab] = useState('active');
    const archived = activeTab === 'archive';
    const [searchString, setSearchString] = useState('');
    const [openAdd, setOpenAdd] = useState(false);
    const [openExport, setOpenExport] = useState(false);

    const [saveAlert, setSaveAlert] = useState(false);
    const [alertState, setAlertState] = useState({ severity: 'error', message: '' });
    // Stable, because the hooks below call it from effects.
    const notify = useCallback((severity: string, message: string) => {
        setAlertState({ severity, message });
        setSaveAlert(true);
    }, []);
    const closeAlert = (_event?: unknown, reason?: string) => {
        if (reason !== 'clickaway') setSaveAlert(false);
    };

    // Polls every minute while the browser tab is visible.
    const { data: cards = NO_CARDS, isPending, errorUpdatedAt, refetch } = useRealtimeQuery({ archived, tempUnit });
    useEffect(() => {
        if (errorUpdatedAt) notify('error', 'Failed to update data. Please try again.');
    }, [errorUpdatedAt, notify]);

    const groups = useDeviceGroups(notify);
    const groupId = groups.selectedGroup ? groups.selectedGroupId : null;
    const members = useGroupMembers({ groupId: groups.selectedGroupId, cards, archived, notify });
    const { moveCard, commitOrder } = useCardReorder(archived, groupId);

    const searching = searchString.trim() !== '';
    const visibleCards = filterVisibleRecords(cards, groupId, searchString);
    const emptyMessage =
        searching && cards.length > 0
            ? t('searchEmptyState')
            : groups.selectedGroup
              ? t('Groups.groupEmptyState')
              : t('emptyState');

    // Groups apply to active devices only and the dropdown is locked on the Archive
    // tab, so entering Archive goes back to All Devices rather than leave a stale
    // selection behind the disabled dropdown.
    const handleTabChange = (tab: string) => {
        if (tab === 'archive') groups.setSelectedGroupId(ALL_DEVICES_ID);
        setActiveTab(tab);
    };

    // Add registers new devices under All Devices; with a custom group selected, it
    // manages that group's members instead.
    const handleOpenAdd = () => {
        if (groups.selectedGroupId === ALL_DEVICES_ID) setOpenAdd(true);
        else members.openDialog();
    };

    return (
        <div className='RealtimePage mt-2 min-h-screen w-full px-4 pb-6 md:mt-8 md:px-6 lg:px-8'>
            <Snackbar open={saveAlert} autoHideDuration={6000} onClose={closeAlert}>
                <Alert onClose={closeAlert} severity={alertState.severity as any}>
                    {alertState.message}
                </Alert>
            </Snackbar>

            <RealtimeToolbarSection
                isMobile={isMobile}
                activeTab={activeTab}
                setActiveTab={handleTabChange}
                searchString={searchString}
                setSearchString={setSearchString}
                // The search itself filters as you type; the button refreshes the grid.
                onSearch={() => refetch()}
                onOpenExport={() => setOpenExport(true)}
                onOpenAdd={handleOpenAdd}
                groups={groups.groups}
                selectedGroupId={groups.selectedGroupId}
                onSelectGroup={groups.setSelectedGroupId}
                onCreateGroup={groups.createGroup}
                onRenameGroup={groups.renameGroup}
                onDeleteGroup={groups.deleteGroup}
            />

            <Add
                openAdd={openAdd}
                setOpenAdd={setOpenAdd}
                setSaveAlert={setSaveAlert}
                setAlertState={setAlertState}
                tKey='RealTime.Add'
                realtime
            />
            <ManageGroupDevicesModal
                open={members.open}
                onClose={members.close}
                groupName={groups.selectedGroup?.name ?? ''}
                groupCompanyId={groups.selectedGroup?.company_id ?? null}
                devices={members.devices}
                memberIds={members.memberIds}
                onConfirm={members.confirm}
            />
            <Export openExport={openExport} setOpenExport={setOpenExport} records={cards} />

            <RealtimeCardGrid
                cards={visibleCards}
                loading={isPending}
                emptyMessage={emptyMessage}
                moveCard={moveCard}
                onDrop={commitOrder}
                // Card order is one shared field per device, so a vendor's drag would rewrite the company's order.
                dragDisabled={searching || isVendor}
                archived={archived}
            />
        </div>
    );
}

export default RealTime;
